import React, { useState, useEffect } from "react";
import {
  SaveProjectInput,
  useGetAllHeadroomProjectsQuery,
  useGetHeadroomProjectByNameQuery,
  useSaveHeadroomProjectMutation,
} from "graphql/types/graphql";
import { context } from "utils/Provider";
import { useHeadroomContext } from "../context/HeadRoomProvider";
import { useErrorSnackbar } from "./useErrorSnackbar";
import { enqueueSnackbar } from "notistack";

export const useHeadroomProjects = () => {
  const { selectedCube } = React.useContext(context);
  const {
    setTableName,
    setSegmentGroups,
    setSelectedProject,
    setDriver1Type,
    setDriver2Type,
    setDriver1NumeratorType,
    setDriver2NumeratorType,
    setDriver1Mode,
    setDriver2Mode,
    setCount1Column,
    setCount2Column,
    setCustomerColumn,
    setDriver1Percentile,
    setDriver2Percentile,
    setHeadroomDriver,
    setShouldSkipSegmentationGroupedCountQuery,
    selectedProject,
    segmentGroups,
    setHeadroomDriverPercentile,
  } = useHeadroomContext();
  const { showError, clearError } = useErrorSnackbar();
  const [limit] = useState(1000);
  const [currentPage, setCurrentPage] = useState(0);
  const [allFetchedProjects, setAllFetchedProjects] = useState<string[]>([]);
  const [projectSearchTerm, setProjectSearchTerm] = useState("");

  const {
    data,
    loading: projectsLoading,
    error: projectsError,
    fetchMore,
    refetch,
  } = useGetAllHeadroomProjectsQuery({
    notifyOnNetworkStatusChange: true,
    variables: {
      cubeId: selectedCube.cubeId,
      limit: limit,
      offset: 0,
    },
    skip: !selectedCube.cubeId,
    fetchPolicy: "network-only",
  });

  const {
    data: projectData,
    loading: projectDetailsLoading,
    error: projectDetailsError,
  } = useGetHeadroomProjectByNameQuery({
    variables: {
      projectName: selectedProject || "",
      cubeId: selectedCube.cubeId,
    },
    skip: !selectedProject || !selectedCube.cubeId,
    fetchPolicy: "network-only",
  });

  const [saveHeadroomProject, { loading: saveLoading, error: saveError }] = useSaveHeadroomProjectMutation();

  useEffect(() => {
    if (projectData?.getHeadroomProjectByName) {
      const project = projectData.getHeadroomProjectByName;

      setTableName(project.tableName || null);
      setSegmentGroups(
        project.segmentGroups?.map((group) => ({
          ...group,
          segments:
            group.segments?.map((segment, idx) => ({
              ...segment,
              id: segment.id,
            })) || [],
        })) || []
      );
      setDriver1Type(project.driver1Type || "");
      setDriver2Type(project.driver2Type || "");
      setDriver1NumeratorType(project.driver1NumeratorType || "");
      setDriver2NumeratorType(project.driver2NumeratorType || "");
      setDriver1Mode(project.driver1Mode || "COLUMN");
      setDriver2Mode(project.driver2Mode || "PERCENTAGE");
      setCount1Column(project.count1Column || "");
      setCount2Column(project.count2Column || "");
      setCustomerColumn(project.customerColumn || "");
      setDriver1Percentile(project.driver1Percentile);
      setDriver2Percentile(project.driver2Percentile);
      setHeadroomDriverPercentile(project.headroomPercentile || 50);

      const headroomDriver = project.headroomDriver;
      if (headroomDriver === "driver1" || headroomDriver === "driver2") {
        setHeadroomDriver(headroomDriver);
      } else {
        setHeadroomDriver(null);
      }

      const hasManualSegments = project.segmentGroups?.some(
        (group) => group.method === "MANUAL" && group.segments?.length > 0
      );
      setShouldSkipSegmentationGroupedCountQuery(!hasManualSegments);
    }
  }, [
    projectData,
    setTableName,
    setSegmentGroups,
    setDriver1Type,
    setDriver2Type,
    setDriver1NumeratorType,
    setDriver2NumeratorType,
    setDriver1Mode,
    setDriver2Mode,
    setCount1Column,
    setCount2Column,
    setCustomerColumn,
    setDriver1Percentile,
    setDriver2Percentile,
    setHeadroomDriver,
    setShouldSkipSegmentationGroupedCountQuery,
    setHeadroomDriverPercentile,
  ]);

  useEffect(() => {
    if (projectsError) {
      showError(projectsError.message || "Failed to fetch projects!");
    } else if (projectDetailsError) {
      showError(projectDetailsError.message || "Failed to fetch project details!");
    } else if (saveError) {
      showError(saveError.message || "Failed to save project!");
    } else {
      clearError();
    }
  }, [projectsError, projectDetailsError, saveError, showError, clearError]);

  useEffect(() => {
    if (selectedCube.cubeId) {
      setCurrentPage(0);
      setAllFetchedProjects([]);
      setSelectedProject(null);
      refetch({
        cubeId: selectedCube.cubeId,
        limit: limit,
        offset: 0,
      });
    }
  }, [selectedCube.cubeId, refetch, limit, setSelectedProject]);

  useEffect(() => {
    if (data?.getAllHeadroomProjects?.edges) {
      const newProjectNames = data.getAllHeadroomProjects.edges?.map((edge) => edge.name ?? "");
      setAllFetchedProjects(newProjectNames);
    }
  }, [data]);

  useEffect(() => {
    return () => {
      clearError();
    };
  }, [clearError]);

  const handleFetchMoreProjects = () => {
    if (!projectsLoading && data?.getAllHeadroomProjects?.hasNextPage) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchMore({
        variables: {
          offset: nextPage * limit,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          return {
            getAllHeadroomProjects: {
              ...fetchMoreResult.getAllHeadroomProjects,
              edges: [
                ...(prev.getAllHeadroomProjects?.edges || []),
                ...(fetchMoreResult.getAllHeadroomProjects?.edges || []),
              ],
            },
          };
        },
      }).catch((fetchError) => {
        showError(fetchError.message || "Failed to fetch more projects!");
      });
    }
  };

  const saveProject = async (input: SaveProjectInput) => {
    try {
      const updatedInput: SaveProjectInput = {
        ...input,
        segmentGroups:
          input.segmentGroups?.map((group) => ({
            ...group,
            segments:
              group.segments?.map((segment, idx) => ({
                ...segment,
                id: segment.id || idx,
              })) || [],
          })) || [],
      };

      const { data: saveData, errors } = await saveHeadroomProject({
        variables: { input: updatedInput },
      });
      if (errors && errors.length > 0) {
        enqueueSnackbar(errors[0].message, { variant: "error" });
      }

      if (saveData?.saveHeadroomProject?.success) {
        if (saveData.saveHeadroomProject.isUpdate) {
          enqueueSnackbar("Project updated successfully!", { variant: "success" });
        } else {
          enqueueSnackbar("Project saved successfully!", { variant: "success" });
        }

        refetch({
          cubeId: selectedCube.cubeId,
          limit: limit,
          offset: 0,
        });
        return saveData.saveHeadroomProject;
      } else {
        showError(saveData?.saveHeadroomProject?.errorMessage || "Failed to save project");
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      showError(errorMessage);
    }
  };

  return {
    projectOptions: data?.getAllHeadroomProjects?.edges.filter((project) =>
      project.name.toLowerCase().includes(projectSearchTerm.toLowerCase())
    ),
    selectedProjectData: projectData?.getHeadroomProjectByName,
    loading: projectsLoading || projectDetailsLoading || saveLoading,
    saveProject,
    projectSearchTerm,
    setProjectSearchTerm,
    handleFetchMoreProjects,
    allFetchedProjects,
  };
};
